/*
 * Copyright (c) 2021. American Express Company
 *
 * FOR INTERNAL USE ONLY. This is not secure enough to use for production.
 * It is a TEMPORARY authentication solution for TESTING PURPOSES ONLY
 */

// third-party
import React, { ChangeEvent, useState } from 'react';
import { ButtonPrimary, CardRounded, FieldControl, Input, Label } from '@americanexpress/dls-react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import amexLogo from 'src/assets/svg/amex-logo.svg';
import AuthService from 'src/services/auth.service';
import StorageService from 'src/services/storage.service';
import { UserUtils } from 'src/types/user.model';
import UserService from 'src/services/user.service';
import NavigationService from 'src/services/navigation.service';
import { useHistory } from 'react-router-dom';
import { goToCurrentBudget } from 'src/services/router.service';
import { History } from 'history';
import { ALLOW_TEST_LOGIN } from 'src/config';
import Selectors from 'src/utils/selectors';

const handleLogin = async (signup: boolean, email: string, history: History<unknown>): Promise<void> => {
    const response = signup ? await AuthService.signupWithEmail(email) : await AuthService.loginTestUser(email);
    // make sure the user id is set if you log in as a demo user
    if (ALLOW_TEST_LOGIN && !StorageService.getTestUserId()) {
        StorageService.setTestUserId(response.userUid);
    }
    // Would be nice to not have to call UserService.getUser() here. One option would be to change the BE to
    // return the information in the response
    if (UserUtils.isBudgetingUser(await UserService.getUser())) {
        goToCurrentBudget(false, history);
    } else {
        NavigationService.goTo(history, '/').catch(console.error);
    }
};

export default function LoginForm(): JSX.Element {
    const history = useHistory();
    const [userEmail, setUserEmail] = useState<string>('');
    return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <CardRounded className="col-md-9 text-align-center" data-cy={Selectors.General.SIGNIN_SCREEN}>
                <div className="login-header">
                    <div className={'margin-1-r'}>
                        <img alt="" src={amexLogo} className="amex-app-logo" />
                    </div>
                    <Typography variant={TypographyVariants.HEADING_S}>
                        Test User Login (LOCAL DEVELOPMENT ONLY)
                    </Typography>
                </div>
                <hr />
                <div className="pad-1 text-align-left">
                    <FieldControl status="default">
                        <Label htmlFor="userEmail" id="userEmail">
                            User Email
                        </Label>
                        <Input
                            id="userEmail"
                            placeholder="userEmail"
                            value={userEmail}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setUserEmail(e.target.value)}
                            required
                            data-cy={Selectors.General.EMAIL_INPUT}
                        />
                    </FieldControl>
                </div>
                <div className="pad-1">
                    <ButtonPrimary
                        type="submit"
                        onClick={async () => await handleLogin(false, userEmail, history)}
                        data-cy={Selectors.General.LOGIN_BUTTON}
                    >
                        Log In
                    </ButtonPrimary>
                    <div style={{ padding: '0.8125rem', display: 'inline-block' }} />
                    <ButtonPrimary
                        type="submit"
                        onClick={async () => await handleLogin(true, userEmail, history)}
                        data-cy={Selectors.General.SIGNUP_BUTTON}
                    >
                        Sign Up
                    </ButtonPrimary>
                </div>
            </CardRounded>
        </div>
    );
}
